const {Component} = require("react");
const PropTypes = require("prop-types");
const maps = require("services/maps");
const geo = require("services/geo");
const {pluckKeysExcept} = require("helpers/object");
const {methodNotImplemented} = require("helpers/errors");
const {createPromiseThrottle} = require("helpers/throttle");
const Promise = require("es6-promise");
const shortid = require("shortid");

/////////////////////////////////
// Helpers
/////////////////////////////////

const addressComponent = (place, type) => {
    const component = place.address_components.filter(componentData => componentData.types.indexOf(type) !== -1) [0];
    return component ? component.long_name : null;
};

const streetComponent = (place) => {
    let parts = [];
    let streetNumber = addressComponent(place, "street_number");
    let streetName = addressComponent(place, "route");

    if (streetName == null) {
        return null;
    }

    parts.push(streetName);

    if (streetNumber != null) {
        parts.push(streetNumber);
    }

    return parts.length > 0 ? parts.join(" ") : null;
};

/////////////////////////////////
// Address field
/////////////////////////////////

class Address extends Component {

    static get propTypes() {
        return {
            types: PropTypes.any,
            componentRestrictions: PropTypes.any,
            handleChange: PropTypes.func,
            guessLocation: PropTypes.bool,
            guessFormat: PropTypes.func
        };
    }

    static get defaultProps() {
        return {
            types: "geocode",
            guessLocation: true
        };
    }

    constructor(props) {
        super(props);

        this.state = {
            value: props.value || props.defaultValue
        };
    }

    get initialValue() {
        return this.props.value || this.props.defaultValue;
    }

    value(name, defaultValue = "") {
        return (this.state.value ? this.state.value[name] : null) || defaultValue;
    }

    get inputProps() {
        return pluckKeysExcept(
            this.props,
            Object.keys(this.constructor.propTypes)
                .concat(["inputRef", "type", "name", "className", "value", "defaultValue", "data-name", "autoComplete", "autocomplete"])
        );
    }

    get className() {
        let classList = [];

        if (typeof this.props.className === "string") {
            classList.push(this.props.className);
        }

        classList.push("maps-autocomplete");

        return classList.join(" ");
    }

    setStateFromPlace(place, callback) {

        if (place == null || place.geometry == null) {
            return this.setEmptyState(callback);
        }

        // Cancel predictions
        this.predict.cancel();

        this.setState({
            value: {
                name: place.formatted_address,
                latitude: place.geometry.location.lat(),
                longitude: place.geometry.location.lng(),
                street: streetComponent(place),
                zip: addressComponent(place, "postal_code"),
                city: addressComponent(place, "sublocality") || addressComponent(place, "locality"),
                country: addressComponent(place, "country")
            }
        }, callback);
    }

    setEmptyState(callback) {
        // Cancel predictions
        this.predict.cancel();

        this.setState({value: null}, callback);
    }

    predict = createPromiseThrottle(250, ({address}) => {
        const componentRestrictions = this.autocompleteOptions.componentRestrictions;

        return new Promise(resolve => {
            this.predictor.geocode(
                {address, componentRestrictions},
                (predictions, status) => resolve({predictions, status})
            );
        });
    });

    _setInputRef = (el) => {
        this.input = el;

        if (typeof this.props.inputRef === "function") {
            this.props.inputRef(el);
        }
    };

    _triggerChange = () => {
        let e = new Event("change");
        e.simulated = true;

        this.input.dispatchEvent(e);
    };

    _handleChange = () => {
        if (typeof this.props.handleChange === "function") {
            this.props.handleChange(this.state.value);
        }
    };

    _guessLocationClick = (e) => {
        e.preventDefault();
        e.stopPropagation();

        this.guessLocation();
    };

    get autocompleteOptions() {
        let options = {};
        let types = this.props.types;
        let componentRestrictions = this.props.componentRestrictions;

        if (typeof types === "string") {
            types = [types];
        }

        if (types instanceof Array) {
            options.types = types;
        }

        if (componentRestrictions && typeof componentRestrictions === "object") {
            options.componentRestrictions = componentRestrictions;
        }

        return options;
    }

    _setupAutocomplete(google) {
        const options = this.autocompleteOptions;
        this.autocomplete = new google.maps.places.Autocomplete(this.input, options);
        this.predictor = new google.maps.Geocoder(options);


        // Bind events
        this.autocomplete.addListener("place_changed", () => {
            this.setStateFromPlace(this.autocomplete.getPlace(), () => {
                this._triggerChange();
                this._handleChange();
            });
        });


    }

    componentDidMount() {
        
        maps.ready(google => {
            this.google = google;
            this._setupAutocomplete(google);

            // Update address when chaning the input
            this.input.addEventListener("change", (e) => {
                if (e.simulated) {
                    return;
                }

                const place = this.autocomplete.getPlace();

                if ((place == undefined) || (place.formatted_address !== this.input.value)) {
                    return this.predict({address: this.input.value}).then((response) => {
                        if ((response.status === "OK") && (response.predictions.length > 0)) {
                            const place = response.predictions[0];

                            this.input.value = this.formatGuess(place);
                            this.setStateFromPlace(place, () => {
                                //this._triggerChange();
                                this._handleChange();
                            });
                        }
                    });
                }
            });

            // Prevent default when pressing ENTER while the address is in focus
            this.input.addEventListener("keypress", e => {
                if ((e.which || e.keyCode) === 13) {
                    e.preventDefault();
                    e.stopPropagation();
                    e.stopImmediatePropagation();
                }
            });

            // Set autocomplete to random stuff (fix Chrome issue with not working autocomplete='off')
            this.input.autocomplete = shortid.generate();
        });

    }

    formatGuess(place) {

        if (typeof this.props.guessFormat === "function") {
            const formatted = this.props.guessFormat(place);

            if (typeof formatted === "string") {
                return formatted;
            }
        }

        let components = {};

        if (typeof(place.address_components) !== 'undefined') {
            for (let i = 0; i < place.address_components.length; i++) {

                let type = place.address_components[i].types[0];
                let value = place.address_components[i].long_name;

                components[type] = value;

            }
        }

        if (typeof(components['subpremise']) !== 'undefined' && typeof(components['postal_code']) !== 'undefined') {
            components['subpremise'] = components['subpremise'].split(',').join('').split(components['postal_code']).join('').trim();
        }

        let address_parts = [];

        if (typeof(components['route']) !== 'undefined') {
            address_parts.push(components['route']);
        }

        if (typeof(components['street_number']) !== 'undefined') {
            address_parts.push(components['street_number']);
        }

        if (typeof(components['subpremise']) !== 'undefined') {
            address_parts.push(components['subpremise']);
        }

        if (typeof(components['political']) !== 'undefined') {
            address_parts.push(', ' + components['political']);
        }

        if (typeof(components['country']) !== 'undefined') {
            address_parts.push(', ' + components['country']);
        }

        //console.log('Components', components);

        if (address_parts.length > 0) {
            return address_parts.join(' ');
        }

        return place.formatted_address;
    }

    guessLocation() {
        const google = this.google;

        geo.getCoordinates((lat, lng) => {
            this.predictor.geocode({latLng: new google.maps.LatLng(lat, lng)}, (results, status) => {
                if (status === 'OK' && results.length > 0) {
                    const place = results[0];

                    this.input.value = this.formatGuess(place);
                    this.setStateFromPlace(place, () => {
                        //this._triggerChange();
                        this._handleChange();
                    });
                }
            });
        });
    }

    renderGuessLocation() {
        if (this.props.guessLocation && geo.isSupported()) {
            return (
                <button type="button" className="guess-location" onClick={this._guessLocationClick}></button>
            );
        }
    }

    render() {
        throw methodNotImplemented("render");
    }

}

module.exports = Address;


// WEBPACK FOOTER //
// ./resources/js/components/abstract/Address.js